import type { ValueGetterParams } from 'ag-grid-community';
import type { ICellRendererParams } from 'ag-grid-enterprise';
import type { BigSource } from 'big.js';
import { numericComparator } from '../../../utils';
import { Box } from '../../Core';
import type { ColumnDef } from './types';

export interface ColorBarProps {
  value: BigSource | undefined;
  content: React.ReactNode;
  barColor: string;
  barWidth: number;
}

export const getColorBarColumn = <T,>({
  colId,
  getColorBarProps,
}: {
  colId: string;
  getColorBarProps: (colId: string, item: T) => ColorBarProps;
}) =>
  ({
    id: colId,
    type: 'custom',
    sortable: true,
    params: {
      headerClass: 'ag-right-aligned-header',
      cellClass: 'ag-right-aligned-cell',
      valueGetter: (params: ValueGetterParams<T>) => {
        if (!params.data) {
          return undefined;
        }
        return getColorBarProps(colId, params.data);
      },
      cellRenderer: ({ value }: ICellRendererParams<T, ColorBarProps>) => {
        if (!value) {
          return null;
        }
        const { content, barColor, barWidth } = value;

        return (
          <Box w="100%" position="relative" py="1px" textAlign="right">
            {barColor != null && barWidth > 0 && (
              <div
                style={{
                  borderRadius: '3px',
                  position: 'absolute',
                  background: barColor,
                  minHeight: '20px',
                  width: `${barWidth * 100}%`,
                  top: '-3px',
                  right: 0,
                }}
              />
            )}
            <Box px="spacing8" position="relative" zIndex={1}>
              {content}
            </Box>
          </Box>
        );
      },
      comparator: (valueA: ColorBarProps, valueB: ColorBarProps) =>
        numericComparator(valueA.value ?? 0, valueB.value ?? 0),
    },
  }) satisfies ColumnDef<T>;
